import React, { useState, useEffect } from 'react';
import { Shield, Mail, Lock, User, Eye, EyeOff, ArrowRight, Github, Globe, BookOpen, Users, Calendar, Star, CheckCircle, AlertCircle, Code, Cpu, Briefcase, Music, Palette, Globe2, FlaskConical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PeacecraftAuth() {
  const [isLogin, setIsLogin] = useState(true);
  const [alert, setAlert] = useState({ message: '', type: '' }); // type: 'success' | 'error'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    agreeToTerms: false,
    interests: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentFeature, setCurrentFeature] = useState(0);
  const navigate = useNavigate();

  const learningInterests = [
    { id: 'programming', name: 'Programming', icon: Code },
    { id: 'ai', name: 'Artificial Intelligence', icon: Cpu },
    { id: 'business', name: 'Business', icon: Briefcase },
    { id: 'design', name: 'Design', icon: Palette },
    { id: 'science', name: 'Science', icon: FlaskConical },
    { id: 'music', name: 'Music', icon: Music },
    { id: 'languages', name: 'Languages', icon: Globe2 },
    { id: 'other', name: 'Other', icon: Globe }
  ];
  

  const features = [
    {
      icon: BookOpen,
      title: "Access 1000+ Books",
      description: "Dive into our curated digital library",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Users,
      title: "Join 50K+ Learners",
      description: "Connect with a global community",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Calendar,
      title: "Live Events Weekly",
      description: "Attend expert-led workshops",
      color: "from-orange-500 to-red-500"
    }
  ];

  
  // Add this useEffect hook at the beginning of your component
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/api/pc/auth/verify', {
          method: 'GET',
          credentials: 'include' // Important for sending HTTP-only cookies
        });

        if (response.ok) {
          // User has valid token, redirect to home
          navigate('/PeaceLanding');
        } else {
          // No valid token, stay on auth page
          //setIsCheckingAuth(false);
        }
      } catch (error) {
        //console.error('Auth verification error:', error);
        //setIsCheckingAuth(false);
      }
    };

    checkAuthStatus();
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInterestChange = (interestId) => {
    setFormData(prev => {
      const newInterests = prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId];
      return { ...prev, interests: newInterests };
    });
  };

  const showAlert = (message, type = 'error') => {
    setAlert({ message, type });
    // Optional: automatically dismiss after 3-5 seconds
    setTimeout(() => {
      setAlert({ message: '', type: '' });
    }, 5000);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    
    
    if (!isLogin) {
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'Please agree to the terms and conditions';
      }

      if (formData.interests.length === 0) {
        newErrors.interests = 'Please select at least one interest';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      agreeToTerms: false,
      interests: []
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;
  setIsLoading(true);

  const url = isLogin ? '/api/pc/auth/login' : '/api/pc/auth/signup';
  
  // Prepare payload
  const payload = isLogin
    ? {
        email: formData.email,
        password: formData.password,
      }
    : {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        agreeToTerms: formData.agreeToTerms,
        interests: formData.interests,
      };
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
  
    if (response.ok) {
      if (isLogin) {       
        window.location.href = '/PeaceLanding';
      } else {
        // Handle signup success - show success message and switch to login
        showAlert(result.message  || 'Account created successfully! Please login.', 'success');
        setIsLogin(true); // This switches the view to login form
        
        // Clear the form (optional)
        setFormData({
          email: formData.email, // Keep email for convenience
          password: '',
          confirmPassword: '',
          fullName: '',
          agreeToTerms: false,
          interests: []
        });
      }
    } else {
      // Show server error message
      showAlert(result.message || 'An error occurred', 'error');
      setErrors({ server: result.message || 'An error occurred' });
    }
  } catch (error) {
    showAlert('Network error. Please try again.', 'error');
    setErrors({ server: 'Network error' });
  } finally {
    setIsLoading(false);
  }
};



  return (
    <div className="min-h-screen bg-slate-900 text-white flex overflow-hidden">
      {/* Animated Background */}
        <div className="fixed inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-60 right-20 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-60 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
        </div>

        {alert.message && (
          <div
            className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 rounded-lg shadow-lg transition-colors duration-300 ${
          alert.type === 'success'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
            }`}
          >
            <div className="flex items-center space-x-2">
          {alert.type === 'success' ? (
            <Star className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="max-w-xs">{alert.message}</span>
            </div>
          </div>
        )}

        {/* Left Side - Features Showcase */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-slate-800 to-slate-900 border-r border-slate-700">
        <div className="flex flex-col justify-center items-center p-12 relative z-10 w-full">
          {/* Logo */}
          <div className="flex items-center space-x-3 mb-12">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Peacecraft
            </span>
          </div>

          {/* Current Feature Display */}
          <div className="text-center mb-12 max-w-md">
            <h2 className="text-4xl font-bold mb-4">
              Welcome to the Future of{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Learning
              </span>
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Join thousands of learners who are transforming their lives through knowledge, community, and innovation.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="space-y-6 w-full max-w-md">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = currentFeature === index;
              return (
                <div
                  key={index}
                  className={`relative p-6 rounded-2xl border transition-all duration-500 ${
                    isActive 
                      ? 'border-slate-500 bg-slate-800/50 scale-105' 
                      : 'border-slate-700 bg-slate-800/20 hover:border-slate-600'
                  }`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 ${isActive ? 'opacity-10' : ''} rounded-2xl transition-opacity duration-500`}></div>
                  <div className="relative z-10 flex items-center space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center ${isActive ? 'scale-110' : ''} transition-transform duration-300`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className="text-slate-400 text-sm">{feature.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Social Proof */}
          <div className="mt-12 flex items-center space-x-6">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full border-2 border-slate-900 flex items-center justify-center text-sm font-bold">
                  {i}
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center space-x-1 mb-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-4 h-4 text-yellow-500 fill-current" />
                ))}
              </div>
              <p className="text-slate-400 text-sm">50,000+ happy learners</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center space-x-2 mb-8">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Peacecraft
            </span>
          </div>

          {/* Form Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">
              {isLogin ? 'Welcome Back!' : 'Join Peacecraft'}
            </h1>
            <p className="text-slate-400">
              {isLogin 
                ? 'Sign in to continue your learning journey' 
                : 'Create your account and start learning today'
              }
            </p>
          </div>

          {/* Social Login */}
          <div className="space-y-3 mb-6">
            <button className="w-full flex items-center justify-center space-x-3 bg-white text-gray-900 py-3 px-4 rounded-xl font-medium hover:bg-gray-100 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
            <button className="w-full flex items-center justify-center space-x-3 bg-gray-800 hover:bg-gray-700 py-3 px-4 rounded-xl font-medium transition-colors">
              <Github className="w-5 h-5" />
              <span>Continue with GitHub</span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-slate-700"></div>
            <span className="px-3 text-slate-500 text-sm">or</span>
            <div className="flex-1 border-t border-slate-700"></div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 bg-slate-800 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                        errors.fullName 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.fullName && (
                    <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.fullName}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      autoComplete='off'
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 bg-slate-800 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                        errors.email 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.email && (
                    <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    autoComplete='off'
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 bg-slate-800 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                      errors.email 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>
            )}

            {!isLogin && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      autoComplete='off'
                      required
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-12 py-3 bg-slate-800 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                        errors.password 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.password}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-12 py-3 bg-slate-800 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                        errors.confirmPassword 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.confirmPassword}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    autoComplete='off'
                    required
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-12 py-3 bg-slate-800 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                      errors.password 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.password}</span>
                  </div>
                )}
              </div>
            )}

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2">What are you interested in learning?</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {learningInterests.map((interest) => {
                    const Icon = interest.icon;
                    const isSelected = formData.interests.includes(interest.id);
                    return (
                      <button
                        type="button"
                        key={interest.id}
                        onClick={() => handleInterestChange(interest.id)}
                        className={`flex items-center space-x-2 p-2 rounded-lg border transition-colors ${
                          isSelected
                            ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                            : 'border-slate-700 hover:border-slate-600 text-slate-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-xs">{interest.name}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.interests && (
                  <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.interests}</span>
                  </div>
                )}
              </div>
            )}

            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" className="rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-400">Remember me</span>
                </label>
                <a href="#" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                  Forgot password?
                </a>
              </div>
            )}

            {!isLogin && (
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className="mt-1 rounded border-slate-600 bg-slate-800"
                  />
                  <span className="text-sm text-slate-400">
                    I agree to the{' '}
                    <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">
                      Privacy Policy
                    </a>
                  </span>
                </label>
                {errors.agreeToTerms && (
                  <div className="flex items-center space-x-1 mt-1 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.agreeToTerms}</span>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="group w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-slate-600 disabled:to-slate-700 py-3 px-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>{isLogin ? 'Sign In' : 'Create Account'}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </div>
          
          {/* Toggle Mode */}
          <div className="text-center mt-6">
            <span className="text-slate-400">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
            </span>
            <button
              onClick={toggleMode}
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}